import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiGlobe, FiSave } from 'react-icons/fi';
import Layout from '../components/Layout';

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3';

const SAVE_PIN = '795842';
const REDIRECT_SERVER_IP = '56.10.19.250';

const CONFIG_FIELDS = [
    {
        name: 'currentWebURL',
        label: 'Current web URL',
        type: 'url',
        placeholder: 'https://example.com',
        note: 'will point SLGWeb server to this url.',
        noteTitle: 'Current Web URL',
    },
    {
        name: 'nextWebURL',
        label: 'Next web URL',
        type: 'url',
        placeholder: 'https://new-example.com',
        note: 'will update SLGWeb middle header.',
        noteTitle: 'Next Web URL',
    },
    {
        name: 'reviewGroup',
        label: 'Review group',
        type: 'text',
        placeholder: 'Telegram group ID or link',
        note: 'will update review footer sent in channel, ReviewWeb group, and #List button.',
        noteTitle: 'Review Group',
    },
    {
        name: 'adminLink',
        label: 'Admin link',
        type: 'url',
        placeholder: 'https://admin.example.com',
        note: 'will update #List admin link, SLGWeb admin username.',
        noteTitle: 'Admin Link',
    },
    {
        name: 'unblockAdminURL',
        label: 'Unblock admin URL',
        type: 'url',
        placeholder: 'https://t.me/example',
        note: 'will update sgunblock admin button.',
        noteTitle: 'Unblock Admin URL',
    },
    {
        name: 'ReviewChannelID',
        label: 'Review channel ID',
        type: 'text',
        placeholder: 'Telegram channel ID',
        note: 'Review sent to channel to this ID.',
        noteTitle: 'ReviewChannel ID',
    },
    {
        name: 'ReviewChannelUsername',
        label: 'Review channel username',
        type: 'text',
        placeholder: '@channelusername',
        note: 'Review footer in channel, ReviewWeb Channel link.',
        noteTitle: 'ReviewChannel Username',
    },
    {
        name: 'ReviewWebsiteUrl',
        label: 'Review website URL',
        type: 'url',
        placeholder: 'https://reviews.example.com',
        note: 'Review footer in Channel.',
        noteTitle: 'Review Website URL',
    },
];

const SAVE_FIELD_NOTES = CONFIG_FIELDS.filter((field) => field.note).map((field) => ({
    label: field.noteTitle || field.label,
    text: field.note,
}));

const SLGWebConfigPage = () => {
    const [formData, setFormData] = useState({
        currentWebURL: '',
        nextWebURL: '',
        reviewGroup: '',
        adminLink: '',
        unblockAdminURL: '',
        ReviewChannelID: '',
        ReviewChannelUsername: '',
        ReviewWebsiteUrl: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [dnsNotice, setDnsNotice] = useState(null);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState('');

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchConfig = async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/slg-web-config`,
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setFormData({
                    currentWebURL: res.data.data?.currentWebURL || '',
                    nextWebURL: res.data.data?.nextWebURL || '',
                    reviewGroup: res.data.data?.reviewGroup || '',
                    adminLink: res.data.data?.adminLink || '',
                    unblockAdminURL: res.data.data?.unblockAdminURL || '',
                    ReviewChannelID: res.data.data?.ReviewChannelID || '',
                    ReviewChannelUsername: res.data.data?.ReviewChannelUsername || '',
                    ReviewWebsiteUrl: res.data.data?.ReviewWebsiteUrl || '',
                });
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load config');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setSuccess('');
    };

    const performSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/slg-web-config`,
                {
                    currentWebURL: formData.currentWebURL.trim(),
                    nextWebURL: formData.nextWebURL.trim(),
                    reviewGroup: formData.reviewGroup.trim(),
                    adminLink: formData.adminLink.trim(),
                    unblockAdminURL: formData.unblockAdminURL.trim(),
                    ReviewChannelID: formData.ReviewChannelID.trim(),
                    ReviewChannelUsername: formData.ReviewChannelUsername.trim(),
                    ReviewWebsiteUrl: formData.ReviewWebsiteUrl.trim(),
                },
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setFormData({
                    currentWebURL: res.data.data?.currentWebURL || '',
                    nextWebURL: res.data.data?.nextWebURL || '',
                    reviewGroup: res.data.data?.reviewGroup || '',
                    adminLink: res.data.data?.adminLink || '',
                    unblockAdminURL: res.data.data?.unblockAdminURL || '',
                    ReviewChannelID: res.data.data?.ReviewChannelID || '',
                    ReviewChannelUsername: res.data.data?.ReviewChannelUsername || '',
                    ReviewWebsiteUrl: res.data.data?.ReviewWebsiteUrl || '',
                });
                setSuccess('Web config saved.');
                if (res.data.currentWebUrlChanged) {
                    setDnsNotice({
                        oldCurrentWebURL: res.data.oldCurrentWebURL || '',
                    });
                }
                if (res.data.slgRedirect && res.data.slgRedirect.ok === false) {
                    const detail =
                        res.data.slgRedirect.reason === 'hub-missing'
                            ? 'sglonelyguyclub-25.site row not found in redirectDomains'
                            : res.data.slgRedirect.reason || 'Redirect sync failed';
                    setError(`Web config saved, but redirect sync failed: ${detail}`);
                }
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to save config');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setPinInput('');
        setPinError('');
        setShowSaveModal(true);
    };

    const handleCloseSaveModal = () => {
        if (saving) return;
        setShowSaveModal(false);
        setPinInput('');
        setPinError('');
    };

    const handleConfirmSave = async () => {
        if (pinInput.trim() !== SAVE_PIN) {
            setPinError('Pin wrong.');
            return;
        }
        setShowSaveModal(false);
        setPinInput('');
        setPinError('');
        await performSave();
    };

    return (
        <Layout>
            <div className="px-4 py-6 max-w-6xl">
                <div className="flex items-center gap-2 mb-6">
                    <FiGlobe className="h-6 w-6 text-blue-500" />
                    <h1 className="text-2xl font-bold">SLG Web Config</h1>
                </div>

                <p className="text-gray-600 mb-6">
                    Set the live consumer site URL and the next URL to switch to (e.g. domain migration).
                </p>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}
                {success && (
                    <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-md">{success}</div>
                )}

                {loading ? (
                    <p className="text-gray-600">Loading...</p>
                ) : (
                    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-6">
                        {CONFIG_FIELDS.map((field) => (
                            <div
                                key={field.name}
                                className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-2 items-start"
                            >
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {field.label}
                                    </label>
                                    <input
                                        type={field.type}
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        placeholder={field.placeholder}
                                        className={inputClass}
                                    />
                                </div>
                                <div className="lg:pt-7">
                                    {field.note ? (
                                        <p className="text-sm text-gray-600 leading-relaxed">
                                            <span className="font-semibold text-gray-800">
                                                {field.noteTitle || field.label}
                                            </span>
                                            {' — '}
                                            {field.note}
                                        </p>
                                    ) : (
                                        <span className="hidden lg:block" aria-hidden="true" />
                                    )}
                                </div>
                            </div>
                        ))}
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                <FiSave className="w-4 h-4" />
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                )}

                {showSaveModal && (
                    <div className="fixed inset-0 z-[200] bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
                        <div className="relative top-10 mx-auto p-6 border w-full max-w-xl shadow-lg rounded-md bg-white mb-10">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm save</h3>
                            <ul className="space-y-3 text-sm text-gray-700 mb-6">
                                {SAVE_FIELD_NOTES.map((item) => (
                                    <li key={item.label}>
                                        <span className="font-semibold text-gray-900">{item.label}</span>
                                        {' — '}
                                        {item.text}
                                    </li>
                                ))}
                            </ul>
                            <div>
                                <label htmlFor="slg-config-save-pin" className="block text-sm font-medium text-gray-700 mb-1">
                                    Pin
                                </label>
                                <input
                                    id="slg-config-save-pin"
                                    type="password"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    value={pinInput}
                                    onChange={(e) => {
                                        setPinInput(e.target.value);
                                        setPinError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleConfirmSave();
                                        }
                                    }}
                                    className={inputClass}
                                    placeholder="Enter pin"
                                />
                                {pinError && (
                                    <p className="mt-2 text-sm text-red-600">{pinError}</p>
                                )}
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={handleCloseSaveModal}
                                    disabled={saving}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmSave}
                                    disabled={saving}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'OK'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {dnsNotice && (
                    <div className="fixed inset-0 z-[210] bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
                        <div className="relative top-20 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-md bg-white mb-10">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">After domain switch</h3>
                            <ol className="space-y-3 text-sm text-gray-800 list-decimal list-inside">
                                <li>
                                    Update &quot;{dnsNotice.oldCurrentWebURL}&quot; A record to {REDIRECT_SERVER_IP}
                                </li>
                                <li>Update Recaptcha Setting</li>
                            </ol>
                            <div className="flex justify-end mt-6">
                                <button
                                    type="button"
                                    onClick={() => setDnsNotice(null)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SLGWebConfigPage;
